import { useImperativeHandle, useRef, type Ref } from 'react'
import { colors, fontSize, radius } from '../../styles/theme'
import { canvasToPng, INK } from './signatureImage'

export interface SignaturePadHandle {
  toPng: () => string
  clear: () => void
}

interface SignaturePadProps {
  ref?: Ref<SignaturePadHandle>
  label: string
  width?: number
  height?: number
  onInkChange: (hasInk: boolean) => void
}

/** Freehand drawing surface; pointer events cover mouse, touch and pen. */
export default function SignaturePad({ ref, label, width = 600, height = 200, onInkChange }: SignaturePadProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const drawing = useRef(false)
  const last = useRef<{ x: number; y: number } | null>(null)

  useImperativeHandle(ref, () => ({
    toPng: () => canvasToPng(canvasRef.current!),
    clear,
  }))

  function point(e: React.PointerEvent<HTMLCanvasElement>) {
    const c = canvasRef.current!
    const r = c.getBoundingClientRect()
    return {
      x: ((e.clientX - r.left) / (r.width || width)) * c.width,
      y: ((e.clientY - r.top) / (r.height || height)) * c.height,
    }
  }

  function ctx() {
    const c = canvasRef.current?.getContext('2d')
    if (!c) return null
    c.strokeStyle = INK
    c.fillStyle = INK
    c.lineWidth = 3
    c.lineCap = 'round'
    c.lineJoin = 'round'
    return c
  }

  function down(e: React.PointerEvent<HTMLCanvasElement>) {
    e.preventDefault()
    canvasRef.current?.setPointerCapture?.(e.pointerId)
    drawing.current = true
    const p = point(e)
    last.current = p
    const c = ctx()
    if (c) { c.beginPath(); c.arc(p.x, p.y, 1.5, 0, Math.PI * 2); c.fill() }
    onInkChange(true)
  }

  function move(e: React.PointerEvent<HTMLCanvasElement>) {
    if (!drawing.current || !last.current) return
    e.preventDefault()
    const p = point(e)
    const c = ctx()
    if (c) { c.beginPath(); c.moveTo(last.current.x, last.current.y); c.lineTo(p.x, p.y); c.stroke() }
    last.current = p
  }

  function up() {
    drawing.current = false
    last.current = null
  }

  function clear() {
    const c = canvasRef.current
    c?.getContext('2d')?.clearRect(0, 0, c.width, c.height)
    onInkChange(false)
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
        <span style={{ fontSize: fontSize.sm, fontWeight: 500, color: colors.text }}>{label}</span>
        <button type="button" onClick={clear} style={{ background: 'none', border: 'none', color: colors.primary, fontSize: fontSize.sm, cursor: 'pointer', padding: 0 }}>
          Clear
        </button>
      </div>
      <canvas
        ref={canvasRef}
        width={width}
        height={height}
        aria-label={`${label} drawing area`}
        onPointerDown={down}
        onPointerMove={move}
        onPointerUp={up}
        onPointerCancel={up}
        onPointerLeave={up}
        style={{
          display: 'block', width: '100%', aspectRatio: `${width} / ${height}`,
          border: `1px dashed ${colors.border}`, borderRadius: radius.md, background: '#fff',
          touchAction: 'none', cursor: 'crosshair',
        }}
      />
    </div>
  )
}
